import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Scaling, Hammer, ArrowRight } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
  })
};

const values = [
  { icon: Target, title: 'Purpose-driven', desc: 'Every solution starts with a clear understanding of your goals and challenges.' },
  { icon: Scaling, title: 'Scalable', desc: 'We build systems designed to grow alongside your business.' },
  { icon: Hammer, title: 'Built for real-world use', desc: 'Practical, robust systems tested for the realities of daily operations.' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section data-testid="about-hero" className="relative py-24 lg:py-32 bg-gradient-to-b from-[#0A0C1C] to-[#12142B] overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E65A14]/5 rounded-full blur-[150px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible">
              <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
                About Us
              </motion.p>
              <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.1]">
                About{' '}
                <span className="bg-gradient-to-r from-[#E65A14] to-[#FFBE50] bg-clip-text text-transparent">
                  EmberCore Digital
                </span>
              </motion.h1>
              <motion.div variants={fadeUp} custom={2} className="mt-8 space-y-5 text-[#C8C8C8] leading-relaxed">
                <p>
                  EmberCore Digital was built on a simple idea: <span className="text-white font-medium">technology should work for your business — not slow it down.</span>
                </p>
                <p>
                  We specialise in designing and developing digital systems that improve efficiency, enhance user experience, and support long-term growth.
                </p>
                <p>
                  With a strong foundation in product thinking and system design, we partner with businesses to build solutions that are not only functional — but impactful.
                </p>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E65A14]/15 to-[#FF8C28]/10 rounded-2xl blur-2xl" />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9003f248-e35e-4fe8-a9ec-ff2fb801281b/images/2c7f77302afccd1412b3c6aa3e951acd6f9a1a847b40965c6963004a505b629d.png"
                alt="Abstract data nodes"
                className="relative rounded-lg border border-[#FF8C28]/20 w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section data-testid="about-mission" className="py-24 lg:py-32 bg-[#0A0C1C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
              Our Mission
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              Empowering businesses with intelligent digital systems
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="mt-6 text-lg text-[#C8C8C8] leading-relaxed">
              To empower businesses with intelligent digital systems that simplify operations and unlock growth.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Approach */}
      <section data-testid="about-approach" className="py-24 lg:py-32 bg-gradient-to-b from-[#0A0C1C] to-[#12142B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
              Our Approach
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              We don't just build software —{' '}
              <span className="bg-gradient-to-r from-[#E65A14] to-[#FFBE50] bg-clip-text text-transparent">
                we solve problems.
              </span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="mt-4 text-[#C8C8C8] max-w-xl mx-auto">
              Every solution we create is:
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                data-testid={`about-value-${i}`}
                className="relative bg-[#12142B] border border-[#FF8C28]/15 rounded-lg p-8 text-center hover:border-[#FF8C28]/40 hover:shadow-[0_0_25px_rgba(230,90,20,0.08)] transition-all duration-300 group"
              >
                <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-[#E65A14]/20 to-transparent border border-[#FF8C28]/20 flex items-center justify-center mb-6 group-hover:border-[#FF8C28]/50 transition-all">
                  <v.icon size={28} className="text-[#FF8C28]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{v.title}</h3>
                <p className="text-sm text-[#C8C8C8] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#12142B]">
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[400px] h-[400px] bg-[#E65A14]/5 rounded-full blur-[100px]" />
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative">
            <motion.h2 variants={fadeUp} custom={0} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              Want to work with us?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="mt-4 text-[#C8C8C8]">
              Let's discuss how we can build something impactful together.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="mt-8">
              <Link
                to="/contact"
                data-testid="about-cta-btn"
                className="inline-flex items-center px-10 py-4 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm hover:shadow-[0_0_30px_rgba(230,90,20,0.5)] transition-all duration-300"
              >
                Let's Talk
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
